import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { IAutocompleteControlProps } from './types';
import { Autocomplete } from '../Autocomplete/Autocomplete';

export const AutocompleteControl = <T extends FieldValues, IOption>(
  props: UseControllerProps<T> & IAutocompleteControlProps<IOption> & { safeDefaultValueOnClear?: boolean },
) => {
  const { safeDefaultValueOnClear, name, control, rules, defaultValue, onChange, onClear, ...restProps } = props;

  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const { ref, value, onChange: onFieldChange, ...restField } = field;
  const { error } = fieldState;

  const getValueObject = (val: IOption | string | null | undefined) => {
    const options = props.options;
    const optionValueKey = (props.optionValueKey ?? 'value') as keyof IOption;

    // Guard against undefined/null values (e.g., clear without defaultValue)
    if (val == null) {
      return null;
    }

    // If options are objects, always map the current value to the latest option object
    if (typeof options?.[0] === 'object') {
      if (typeof val === 'object') {
        const targetValue = (val as IOption)[optionValueKey];
        const matched = options.find((option) => option[optionValueKey] === targetValue);
        return matched ?? val;
      } else {
        // For primitive values, compare directly against the optionValueKey
        const matched = options.find(
          (option) => (option as unknown as { [k: string]: unknown })[optionValueKey as string] === val,
        );
        return matched ?? (val as unknown as IOption | string);
      }
    }

    // Options are primitives (strings), return as-is
    return val;
  };

  const fieldValue = getValueObject(value) || value;

  const handleChange = (e?: string | IOption | (string | IOption)[] | null) => {
    onFieldChange(e);

    if (onChange && e) onChange(e);
  };

  const onFieldClear = () => {
    const defaultOption = getValueObject(defaultValue as IOption | string);

    if (safeDefaultValueOnClear && defaultOption) {
      onFieldChange(defaultOption);
      onChange?.(defaultOption);
      return;
    }

    if (defaultOption) {
      onFieldChange(null);
      onChange?.(null);
      return;
    }
    onClear?.();
  };

  return (
    <Autocomplete
      {...restField}
      {...restProps}
      value={fieldValue}
      inputRef={ref}
      fieldError={error}
      name={name}
      onChange={handleChange}
      onFieldClear={onFieldClear}
    />
  );
};
